/**
 * Global constructor for PaymentCheckout
 * Provides a fluent API for creating checkout instances
 */

import type { CheckoutConfig } from '../types';
import { parseDataAttributes } from './attributeParser';
import { PaymentCheckout } from '../PaymentCheckout';

export interface GlobalCheckoutConfig extends CheckoutConfig {}

export class GlobalCheckoutBuilder {
  private config: Partial<GlobalCheckoutConfig> = {};

  creatorId(id: string): this {
    this.config.creatorId = id;
    return this;
  }

  productId(id: string): this {
    this.config.productId = id;
    return this;
  }

  bumpProductIds(ids: string[]): this {
    this.config.bumpProductIds = ids;
    return this;
  }

  couponCode(code: string): this {
    this.config.couponCode = code;
    return this;
  }

  affiliateCode(code: string): this {
    this.config.affiliateCode = code;
    return this;
  }

  checkoutSessionSecret(secret: string): this {
    this.config.checkoutSessionSecret = secret;
    return this;
  }

  environment(env: 'sandbox' | 'production'): this {
    this.config.environment = env;
    return this;
  }

  overrideBaseUrl(url: string): this {
    this.config.overrideBaseUrl = url;
    return this;
  }

  theme(theme: GlobalCheckoutConfig['theme']): this {
    this.config.theme = theme!;
    return this;
  }

  containerOptions(options: { width?: string; height?: string }): this {
    this.config.containerOptions = options!;
    return this;
  }

  redirectSettings(settings: GlobalCheckoutConfig['redirectSettings']): this {
    this.config.redirectSettings = settings!;
    return this;
  }

  showSubmitButton(show: boolean): this {
    this.config.showSubmitButton = show;
    return this;
  }

  build(): any {
    if (!this.config.creatorId) {
      throw new Error('Creator ID is required. Use .creatorId() to set it.');
    }
    if (!this.config.productId) {
      throw new Error('Product ID is required. Use .productId() to set it.');
    }
    if (!this.config.checkoutSessionSecret) {
      throw new Error('Checkout session secret is required. Use .checkoutSessionSecret() to set it.');
    }

    // Set default environment if not provided
    if (!this.config.environment) {
      this.config.environment = 'sandbox';
    }

    // Import PaymentCheckout dynamically
    return new PaymentCheckout(this.config as GlobalCheckoutConfig);
  }

  attachTo(element: HTMLElement): Promise<any> {
    return this.build().then((checkout: any) => {
      checkout.attachToElement(element);
      return checkout;
    });
  }
}

export interface GlobalPaymentCheckout {
  create(config?: GlobalCheckoutConfig): PaymentCheckout | GlobalCheckoutBuilder;
  fromElement(element: HTMLElement, config?: GlobalCheckoutConfig): Promise<any>;
  autoInitialize(selector?: string): void;
  loadScript(url?: string): Promise<void>;
  isAvailable(): boolean;
}

export function initializeGlobalConstructor(): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Check if already initialized
  if ((window as any).PaymentCheckout) {
    return;
  }

  const PaymentCheckoutGlobal: GlobalPaymentCheckout = {
    create(configOrBuilder?: GlobalCheckoutConfig): PaymentCheckout | GlobalCheckoutBuilder {
      if (configOrBuilder) {
        // Direct config creation
        return new PaymentCheckout(configOrBuilder);
      } else {
        // Fluent builder
        return new GlobalCheckoutBuilder();
      }
    },

    fromElement(element: HTMLElement, config?: GlobalCheckoutConfig): Promise<any> {
      if (config) {
        return import('../PaymentCheckout').then(({ PaymentCheckout }) => {
          return PaymentCheckout.fromElement(element, config);
        });
      }
      
      // Try to parse config from data attributes
      const parsedConfig = parseDataAttributes(element);
      
      if (!parsedConfig.creatorId || !parsedConfig.productId || !parsedConfig.checkoutSessionSecret) {
        throw new Error('Element must have data-creator-id, data-product-id, and data-checkout-session-secret attributes, or provide config parameter');
      }

      // create wrapper element
      element.innerHTML = '';
      const wrapperElement = document.createElement('div');
      wrapperElement.className = 'fb-checkout-wrapper';
      wrapperElement.style.display = 'flex';
      wrapperElement.style.width = '100%';
      wrapperElement.style.height = '100%';
      element.appendChild(wrapperElement);
      
      return import('../PaymentCheckout').then(({ PaymentCheckout }) => {
        const checkout = PaymentCheckout.fromElement(wrapperElement, parsedConfig);
        checkout.init();
        return checkout;
      });
    },

    autoInitialize(selector = '[data-checkout]'): void {
      // Auto-initialization logic would go here
      console.log('Auto-initialization not yet implemented');
    },

    loadScript(url?: string): Promise<void> {
      return new Promise((resolve, reject) => {
        if (document.querySelector('script[src*="checkout"]')) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = url || 'https://cdn.fanbasis.com/checkout/latest.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load checkout script'));
        
        document.head.appendChild(script);
      });
    },

    isAvailable(): boolean {
      return typeof (window as any).PaymentCheckout !== 'undefined';
    }
  };

  // Expose to global scope
  (window as any).PaymentCheckout = PaymentCheckoutGlobal;
  (window as any).FanbasisCheckout = PaymentCheckoutGlobal; // Alternative name
}

// Note: parseDataAttributes is now imported from attributeParser.ts 